import { Box, Dialog, Flex, Portal, Stack, Text, chakra } from '@chakra-ui/react';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

interface HelpModalProps {
  open: boolean;
  onClose: () => void;
}

export function HelpModal({ open, onClose }: HelpModalProps) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(details) => {
        if (!details.open) {
          onClose();
        }
      }}
      placement="center"
      size={{ mdDown: 'full', md: 'lg' }}
    >
      <Portal>
        <Dialog.Backdrop
          position="fixed"
          inset="0"
          zIndex="modal"
          bg="blackAlpha.700"
          backdropFilter="blur(4px)"
        />
        <Dialog.Positioner
          position="fixed"
          inset="0"
          zIndex="modal"
          display="grid"
          placeItems="center"
          px="4"
          py="8"
        >
          <Dialog.Content
            maxH={{ base: 'calc(100vh - 4rem)', md: '720px' }}
            w="full"
            maxW="2xl"
            overflowY="auto"
            borderRadius="oqPanel"
            borderWidth="1px"
            borderColor="border.subtle"
            bg="surface.panel"
            p="5"
            boxShadow="2xl"
          >
            <Flex
              align="flex-start"
              justify="space-between"
              gap="4"
              borderBottomWidth="1px"
              borderBottomColor="border.subtle"
              pb="4"
            >
              <Stack gap="1">
                <Dialog.Title fontSize="xl" fontWeight="semibold" color="text.primary">
                  Oddsquake Help
                </Dialog.Title>
                <Text fontSize="sm" color="text.muted">
                  A quick guide to the live dashboard language.
                </Text>
              </Stack>
              <Dialog.CloseTrigger asChild>
                <chakra.button
                  type="button"
                  aria-label="Close help"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="border.subtle"
                  p="2"
                  color="text.muted"
                  transition="all 0.2s"
                  _hover={{ color: 'text.primary' }}
                >
                  <X size={16} />
                </chakra.button>
              </Dialog.CloseTrigger>
            </Flex>

            <Box
              mt="5"
              borderRadius="md"
              borderWidth="1px"
              borderColor="signal.blue/20"
              bg="brand.subtle"
              p="4"
              fontSize="sm"
              lineHeight="6"
              color="text.muted"
            >
              <Text fontWeight="semibold" color="text.primary">
                About Oddsquake
              </Text>
              <Text mt="1">
                Oddsquake tracks real-time prediction-market odds swings and highlights quake events, related markets,
                catalysts, and external news matches.
              </Text>
              <Text mt="3">
                Created by{' '}
                <chakra.a
                  href="https://tulio.org"
                  target="_blank"
                  rel="noreferrer noopener me"
                  fontWeight="medium"
                  color="text.primary"
                  transition="all 0.2s"
                  _hover={{ color: 'signal.blue' }}
                >
                  Túlio Ribeiro dos Anjos
                </chakra.a>
                .{' '}
                <chakra.a
                  href="https://github.com/tulior"
                  target="_blank"
                  rel="noreferrer noopener me"
                  fontWeight="medium"
                  color="text.primary"
                  transition="all 0.2s"
                  _hover={{ color: 'signal.blue' }}
                >
                  GitHub
                </chakra.a>
              </Text>
            </Box>

            <Stack mt="5" gap="3" fontSize="sm" lineHeight="6" color="text.muted">
              <HelpItem title="What is a mover?">
                A market outcome whose live price changed enough inside the selected window to rank in the feed.
              </HelpItem>
              <HelpItem title="What is a quake event?">
                A persisted mover that clears the backend score, change, volume, spread, and cooldown thresholds.
              </HelpItem>
              <HelpItem title="What is a catalyst?">
                A real event attached to a quake, such as related-market movement, a timing boundary, a liquidity warning, or matched external news.
              </HelpItem>
              <HelpItem title="What does Catalyst Match mean?">
                It is the share of today's quake markets that have at least one qualifying catalyst event.
              </HelpItem>
              <HelpItem title="Why can rows be empty?">
                The table reserves slots to keep the page stable while live movers appear, disappear, or fail the active filters.
              </HelpItem>
              <HelpItem title="What data is live versus inferred?">
                Prices, spreads, volume, depth, markets, quakes, and catalysts come from the backend. Scores, labels, and related-market signals are backend inferences from that live data.
              </HelpItem>
              <HelpItem title="When does external news appear?">
                External news appears only when Google News RSS or GDELT enrichment is enabled and matched to a quake.
              </HelpItem>
            </Stack>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

function HelpItem({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box borderRadius="md" borderWidth="1px" borderColor="border.subtle" bg="blackAlpha.100" p="4">
      <Text fontWeight="semibold" color="text.primary">
        {title}
      </Text>
      <Text mt="1">{children}</Text>
    </Box>
  );
}